import React from "react";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { checkIfDataInCurrentMonthExists } from "utils/time-utils";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { Instant } from "@js-joda/core";
import { safeIdString } from "utils/utils";
import { useTimezone } from "hooks/useTimezone";
import { DataDisplayType, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import NoDataInCurrentMonth from "./NoDataInCurrentMonth";
import DatasetValueDisplay from "./DatasetValueDisplay";

const DatasetValue = (
  props: ValueChangeIndicatorProps & {
    dataset: DatasetDataInfoResponse;
    diffDecimalPlaces?: number;
    refTime: Instant;
    skipMonthCheck?: boolean;
    setDialog: (isVisible: boolean) => void;
  }
) => {
  const tz = useTimezone();
  const safeId = safeIdString(props.dataset.identifier);
  let val: InstantDoubleValue;

  if (props.dataDisplayType === DataDisplayType.SUM_OF_VALUES && props.dataset.data.length > 0) {
    val = props.dataset.data.reduce(
      (acc: InstantDoubleValue, curr: InstantDoubleValue) => ({
        key: curr.key,
        value: (acc.value ?? 0) + (curr.value ?? 0),
      }),
      { key: props.dataset.data[0].key, value: 0 }
    );
  } else {
    val = props.dataset.data?.[1] ?? props.dataset.data?.[0];
  }

  const isMultiple = props.datasets.length > 1 && !props.displayInline;
  if (props.hideZeroValues && val?.value === 0) {
    return null;
  }

  if (!val || val.key == null) {
    return <NoDataInCurrentMonth tz={tz.company} refTime={props.refTime} theme={props.theme} />;
  }

  const inCurrentMonth = checkIfDataInCurrentMonthExists(val.key, tz.company, props.refTime);
  if (!inCurrentMonth && !props.skipMonthCheck && props.dataDisplayType !== DataDisplayType.SUM_OF_VALUES) {
    return <NoDataInCurrentMonth tz={tz.company} refTime={props.refTime} theme={props.theme} />;
  }

  return (
    <DatasetValueDisplay
      val={val}
      id={safeId}
      highlightValue={props.highlightValue}
      theme={props.theme}
      unit={props.dataset.unit}
      tz={tz.company}
      decimalPlaces={props.decimalPlaces}
      diffDecimalPlaces={props.diffDecimalPlaces}
      convertToAbsoluteValues={props.convertToAbsoluteValues}
      isMultiple={isMultiple}
      setDialog={props.setDialog}
    />
  );
};

export default DatasetValue;
